import { React, useState } from 'react';
import { mutate } from 'swr';

import { Badge, Button, Dialog, Pane, Switch, Table, TextInput, TrashIcon } from 'evergreen-ui';

import {
  deleteCategory,
  disableAllCategories,
  putCategory,
  useQueryCategories,
} from '../../../services/api/categoriesService';

import CategoryFormComponent from './CategoryFormComponent';
import { toastNotification } from '../../../shared/toastNotification';
import CategoryEditFormComponent from './CategoryEditFormComponent';
import { useEffect } from 'react';
import LoadingOverlay from '../../../shared/LoadingOverlay';

function CategoriesComponent() {
  const companyId = localStorage.getItem('companyId');
  /* API calls */

  const { data: categories = [], isLoading: isLoadingCategories } = useQueryCategories(
    // company.data.id,
    companyId,
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const [isShownDialog, setIsShownDialog] = useState(false);
  const [isShownEditDialog, setIsShownEditDialog] = useState(false);
  const [isShownDisableAllDialog, setIsShownDisableAllDialog] = useState(false);
  const [isDisablingAll, setIsDisablingAll] = useState(false);
  const [categoryOnFocus, setCategoryOnFocus] = useState();
  const [localSearch, setLocalSearch] = useState('');

  const openDialog = () => {
    setIsShownDialog(true);
  };
  const openEditDialog = (category) => {
    setIsShownEditDialog(true);
    setCategoryOnFocus(category);
  };

  const onDeleteCategory = (id) => {
    console.log('delete id: ', id);
    let statusResponse = false;
    let toastContent = '';
    deleteCategory(id)
      .then(() => {
        mutate(`categories-${companyId}`);
        statusResponse = 'success';
        toastContent = 'Categoria deletada com sucesso.';
      })
      .catch(() => {
        statusResponse = 'warning';
        toastContent = `Erro. Verifique os campos novamente.`;
      })
      .finally(() => {
        toastNotification(statusResponse, toastContent);
      });
  };

  const onToggleEnabled = (category) => {
    const nextEnabled = !category.enabled;
    const key = `categories-${companyId}`;

    // Atualização otimista: reflete na tela na hora do clique, sem esperar a rede.
    mutate(
      key,
      (current) =>
        current?.data
          ? {
              ...current,
              data: current.data.map((c) =>
                c.id === category.id ? { ...c, enabled: nextEnabled } : c
              ),
            }
          : current,
      false
    );

    putCategory({ ...category, enabled: nextEnabled })
      .then(() => {
        mutate(key);
      })
      .catch(() => {
        toastNotification('warning', 'Erro ao atualizar categoria.');
        mutate(key); // reverte para o estado real do servidor
      });
  };

  const onDisableAllCategories = () => {
    setIsDisablingAll(true);
    disableAllCategories(companyId)
      .then(() => {
        mutate(`categories-${companyId}`);
        toastNotification('success', 'Todas as categorias foram desabilitadas.');
        setIsShownDisableAllDialog(false);
      })
      .catch(() => {
        toastNotification('warning', 'Erro ao desabilitar categorias.');
      })
      .finally(() => {
        setIsDisablingAll(false);
      });
  };

  useEffect(() => {
    mutate(`categories-${companyId}`);
  }, [companyId]);

  if (isLoadingCategories) {
    return <LoadingOverlay />;
  }

  return (
    <>
      <CategoryFormComponent isShown={isShownDialog} setIsShown={setIsShownDialog} />
      <CategoryEditFormComponent
        isShown={isShownEditDialog}
        setIsShown={setIsShownEditDialog}
        category={categoryOnFocus}
      />
      <Dialog
        isShown={isShownDisableAllDialog}
        title="Desabilitar todas as categorias"
        intent="danger"
        onCloseComplete={() => setIsShownDisableAllDialog(false)}
        confirmLabel="Desabilitar todas"
        cancelLabel="Cancelar"
        isConfirmLoading={isDisablingAll}
        onConfirm={onDisableAllCategories}
      >
        Tem certeza que deseja desabilitar todas as categorias desta empresa? Você poderá
        habilitar novamente, uma a uma, pelo switch da lista.
      </Dialog>
      <Pane display="flex" flexDirection="column" gap={6}>
        <Pane display="flex" gap={8} marginBottom={8}>
          <Button appearance="primary" intent="info" maxWidth="fit-content" onClick={openDialog}>
            Criar Categoria
          </Button>
          <Button
            intent="danger"
            maxWidth="fit-content"
            onClick={() => setIsShownDisableAllDialog(true)}
          >
            Desabilitar todas
          </Button>
        </Pane>
        <TextInput
          placeholder="Filtrar"
          marginBottom={12}
          width="200px"
          onChange={(e) => setLocalSearch(e.target.value.toLowerCase())}
        />
      </Pane>
      <Table>
        <Table.Head>
          <Table.TextHeaderCell>Nome</Table.TextHeaderCell>
          <Table.TextHeaderCell>Plano de Conta</Table.TextHeaderCell>
          <Table.TextHeaderCell>Tipo</Table.TextHeaderCell>
          <Table.TextHeaderCell>Habilitado</Table.TextHeaderCell>
          <Table.TextHeaderCell>Ações</Table.TextHeaderCell>
        </Table.Head>
        <Table.Body>
          {!isLoadingCategories &&
            categories.data
              ?.sort((a, b) => {
                const nameA = a.name.toUpperCase();
                const nameB = b.name.toUpperCase();

                if (nameA < nameB) {
                  return -1;
                }

                if (nameA > nameB) {
                  return 1;
                }

                return 0;
              })
              .map((category) => {
                if (category.isInput) {
                  category.entryType = 'Entrada';
                } else {
                  category.entryType = 'Saída';
                }
                if (
                  category.name.toLowerCase().includes(localSearch) ||
                  category.accountPlan?.name.toLowerCase().includes(localSearch) ||
                  category.entryType.toLowerCase().includes(localSearch)
                )
                  return (
                    <Table.Row
                      key={category.id}
                      isSelectable
                      onSelect={() => openEditDialog(category)}
                    >
                      <Table.TextCell>{category.name}</Table.TextCell>
                      <Table.TextCell>{category.accountPlan?.name}</Table.TextCell>
                      <Table.TextCell>
                        {category.isInput ? (
                          <Badge color="blue">Entrada</Badge>
                        ) : (
                          <Badge color="red">Saída</Badge>
                        )}
                      </Table.TextCell>
                      <Table.TextCell onClick={(event) => event.stopPropagation()}>
                        <Switch
                          checked={!!category.enabled}
                          onChange={() => onToggleEnabled(category)}
                        />
                      </Table.TextCell>
                      <Table.TextCell
                        onClick={(event) => {
                          event.stopPropagation();
                          onDeleteCategory(category.id);
                        }}
                      >
                        <TrashIcon className="trash-icon" />
                      </Table.TextCell>
                    </Table.Row>
                  );
                return null;
              })}
        </Table.Body>
        <style scoped jsx="true">{`
          .trash-icon:hover {
            color: red;
          }
        `}</style>
      </Table>
    </>
  );
}

export default CategoriesComponent;
